import { useTranslation } from 'react-i18next';
import { Badge } from '@/expenses/list/expense-labels';
import { formatWhen, shortId } from '../format';
import { useLabels } from '../labels';
import type { LogRow } from './rows';

const STATUS_TONE: Readonly<Record<string, 'success' | 'error' | 'neutral'>> = {
  sent: 'success',
  failed: 'error',
};

const HEAD = 'px-3 py-2.5 font-semibold';

/** What the row says: the text version of a message, or the signal's name in words. */
export function useDetail() {
  const { t } = useTranslation('communications');
  return (item: LogRow): string =>
    item.kind === 'message'
      ? t('log.version', { label: item.label })
      : t(`log.signal.${item.label}`, { defaultValue: item.label });
}

function Status({ item }: { readonly item: LogRow }) {
  const { t } = useTranslation('communications');
  if (item.status === null) return <span className="text-muted-foreground">—</span>;
  const tone = STATUS_TONE[item.status] ?? 'neutral';
  return <Badge tone={tone}>{t(`log.status.${item.status}`, { defaultValue: item.status })}</Badge>;
}

/** Desktop: one row per message or signal. */
export function LogTable({ items }: { readonly items: readonly LogRow[] }) {
  const { t, i18n } = useTranslation('communications');
  const detail = useDetail();
  const labels = useLabels();
  const language = i18n.resolvedLanguage;
  const heads = [
    'when',
    'kind',
    'user',
    'profile',
    'step',
    'detail',
    'status',
    'approvedBy',
  ] as const;
  return (
    <div className="border-border hidden overflow-x-auto rounded-xl border @min-[620px]/shell:block">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t('log.title')}</caption>
        <thead className="bg-muted/50 text-muted-foreground text-left text-xs tracking-wide uppercase">
          <tr>
            {heads.map((head) => (
              <th key={head} scope="col" className={HEAD}>
                {t(`log.columns.${head}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-border hover:bg-accent/50 border-t">
              <td className="px-3 py-3 whitespace-nowrap tabular-nums">
                {formatWhen(item.at, language)}
              </td>
              <td className="px-3 py-3">{t(`log.kind.${item.kind}`)}</td>
              <td className="px-3 py-3 font-semibold" title={item.userId}>
                {shortId(item.userId)}
              </td>
              <td className="px-3 py-3">{labels.profile(item.profile)}</td>
              <td className="px-3 py-3">{labels.step(item.step)}</td>
              <td className="px-3 py-3">{detail(item)}</td>
              <td className="px-3 py-3">
                <Status item={item} />
              </td>
              <td className="px-3 py-3">
                {item.approvedBy === null ? '—' : shortId(item.approvedBy)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phone: a card per message or signal. */
export function LogCards({ items }: { readonly items: readonly LogRow[] }) {
  const { t, i18n } = useTranslation('communications');
  const detail = useDetail();
  const labels = useLabels();
  return (
    <ul aria-label={t('log.title')} className="flex flex-col gap-2 @min-[620px]/shell:hidden">
      {items.map((item) => (
        <li
          key={item.id}
          className="border-border bg-card flex flex-col gap-1.5 rounded-xl border p-3"
        >
          <div className="flex items-start justify-between gap-3">
            <b title={item.userId}>{shortId(item.userId)}</b>
            <Status item={item} />
          </div>
          <p className="text-sm">{detail(item)}</p>
          <p className="text-muted-foreground text-[13px]">
            {t(`log.kind.${item.kind}`)} · {labels.profile(item.profile)} · {labels.step(item.step)}
            {item.approvedBy === null ? '' : ` · ${shortId(item.approvedBy)}`}
          </p>
          <p className="text-muted-foreground text-[13px] tabular-nums">
            {formatWhen(item.at, i18n.resolvedLanguage)}
          </p>
        </li>
      ))}
    </ul>
  );
}
